import { useCallback, useEffect, useMemo, useState } from 'react'
import { describePlatform, type PlatformInfo } from '@/lib/platform'
import { usePushSubscription, type PushSubscriptionState } from './usePushSubscription'

const STORAGE_KEY = 'hermes.onboarding.v1'
const NUDGE_KEY = 'hermes.install-nudge.v1'

/**
 * Set by the signup page on a successful redemption.
 *
 * Existing users who simply log in should not be shown a "welcome, here is how
 * this works" step — only someone whose account was just created.
 */
export const JUST_SIGNED_UP_KEY = 'hermes.just-signed-up.v1'

/** How long a dismissed install nudge stays quiet. */
const NUDGE_SNOOZE_DAYS = 7

interface StoredState {
  /** Set when the user finished or skipped the guided flow. */
  seenAt?: number
  /** Set when the install nudge was last dismissed. */
  nudgeDismissedAt?: number
}

function read(key: string): StoredState {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as StoredState) : {}
  } catch {
    // Private mode, or storage disabled. Treat as "never seen" rather than
    // failing — the worst case is being asked again.
    return {}
  }
}

function write(key: string, value: StoredState): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Nothing to do; the flow still works, it just will not be remembered.
  }
}

export type OnboardingStep = 'welcome' | 'install' | 'notifications' | 'done'

export interface OnboardingState {
  /** True when the guided flow should be shown. */
  open: boolean
  steps: OnboardingStep[]
  platform: PlatformInfo
  push: PushSubscriptionState
  /** True when a mobile user is browsing in a tab rather than the installed app. */
  showInstallNudge: boolean
  dismissNudge: () => void
  start: () => void
  finish: () => void
}

/**
 * Decides whether a user needs onboarding, and what it should cover.
 *
 * The steps are derived from what is actually missing rather than being a fixed
 * script: someone who already installed the app and allowed notifications is
 * shown nothing, and an iOS user in a normal tab is shown the install step
 * first because notifications are literally unreachable until they do it.
 */
export function useOnboarding(): OnboardingState {
  const push = usePushSubscription()
  const [platform, setPlatform] = useState<PlatformInfo>(() => describePlatform())
  const [stored, setStored] = useState<StoredState>(() => read(STORAGE_KEY))
  const [nudge, setNudge] = useState<StoredState>(() => read(NUDGE_KEY))
  const [forcedOpen, setForcedOpen] = useState(false)

  // Read once on mount: the flag is cleared when the flow finishes, and
  // re-reading would make the welcome step vanish mid-flow.
  const [justSignedUp] = useState(() => {
    try {
      return localStorage.getItem(JUST_SIGNED_UP_KEY) === '1'
    } catch {
      return false
    }
  })

  // Installing changes display-mode without a reload, so the platform facts
  // have to be re-read rather than captured once.
  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)')
    const update = () => setPlatform(describePlatform())
    media.addEventListener('change', update)
    window.addEventListener('appinstalled', update)
    return () => {
      media.removeEventListener('change', update)
      window.removeEventListener('appinstalled', update)
    }
  }, [])

  const steps = useMemo<OnboardingStep[]>(() => {
    const list: OnboardingStep[] = []
    if (justSignedUp) list.push('welcome')
    if (!platform.standalone && platform.installMethod !== 'none') list.push('install')
    if (push.status !== 'subscribed' && push.status !== 'denied') list.push('notifications')
    list.push('done')
    return list
  }, [platform, push.status, justSignedUp])

  // Nothing to teach: installed (or not installable) and notifications settled.
  const hasWork = steps.length > 1 || justSignedUp

  const open = forcedOpen || (!stored.seenAt && hasWork && push.status !== 'checking')

  const finish = useCallback(() => {
    const next = { ...read(STORAGE_KEY), seenAt: Date.now() }
    write(STORAGE_KEY, next)
    setStored(next)
    setForcedOpen(false)
    try {
      localStorage.removeItem(JUST_SIGNED_UP_KEY)
    } catch {
      // Nothing to clean up if storage is unavailable.
    }
  }, [])

  const start = useCallback(() => setForcedOpen(true), [])

  const dismissNudge = useCallback(() => {
    const next = { ...read(NUDGE_KEY), nudgeDismissedAt: Date.now() }
    write(NUDGE_KEY, next)
    setNudge(next)
  }, [])

  /*
   * The standing nudge, separate from the one-time flow.
   *
   * A mobile user in a browser tab is missing the thing the app is for —
   * notifications do not work on iOS outside the installed app — so it is worth
   * mentioning again after a snooze, unlike the guided flow which is shown once.
   */
  const snoozeExpired =
    !nudge.nudgeDismissedAt || Date.now() - nudge.nudgeDismissedAt > NUDGE_SNOOZE_DAYS * 86_400_000

  const showInstallNudge =
    !open &&
    platform.mobile &&
    !platform.standalone &&
    platform.installMethod !== 'none' &&
    Boolean(stored.seenAt) &&
    snoozeExpired

  return { open, steps, platform, push, showInstallNudge, dismissNudge, start, finish }
}
